import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CreditCard, User, Sparkles, Lock, AlertTriangle, X, RotateCcw } from "lucide-react";

// Order plans by ascending price so upgrade/downgrade classification lines up
// with visual position. Backend also uses this order in _PLAN_RANK.
const rankPlans = (plans) => plans.slice().sort((a, b) => a.price_monthly - b.price_monthly);

export default function SettingsPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [billingAccess, setBillingAccess] = useState(null);
  const [busyPlanId, setBusyPlanId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const refresh = async () => {
    const [meRes, subRes] = await Promise.all([
      api.get("/auth/me"),
      api.get("/paypal/subscription").catch(() => ({ data: null })),
    ]);
    setCurrent(meRes.data);
    setSubscription(subRes.data);
  };

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(rankPlans(r.data)));
    api.get("/billing/access").then((r) => setBillingAccess(r.data)).catch(() => setBillingAccess({ allowed: false, reason: "unknown" }));
    refresh();
  }, []);

  // PayPal bounce-back: ?paypal=success&subscription_id=I-…
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paypal") !== "success") return;
    const subId = params.get("subscription_id");
    if (!subId) return;
    (async () => {
      try {
        const { data } = await api.post("/paypal/return", { subscription_id: subId });
        toast.success(`Plan activated: ${data.plan}`);
        await refresh();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not confirm subscription.");
      } finally {
        window.history.replaceState({}, "", window.location.pathname);
      }
    })();
  }, []);

  const currentPlanId = current?.plan || "trial";
  const currentIsPaid = currentPlanId !== "trial";
  const currentPlanIndex = useMemo(() => plans.findIndex((p) => p.id === currentPlanId), [plans, currentPlanId]);
  const isCancelScheduled = !!subscription?.cancel_at_period_end;

  // Kind of change (new/upgrade/downgrade/current) — drives button label.
  const changeKind = (planId) => {
    if (planId === currentPlanId) return "current";
    if (!currentIsPaid) return "subscribe";
    const idx = plans.findIndex((p) => p.id === planId);
    return idx > currentPlanIndex ? "upgrade" : "downgrade";
  };

  const subscribe = async (planId) => {
    setBusyPlanId(planId);
    try {
      const { data } = await api.post("/paypal/subscribe", { plan_id: planId });
      if (data.approval_url) {
        window.location.href = data.approval_url;
      } else {
        toast.success("Plan changed.");
        await refresh();
        setBusyPlanId(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start PayPal checkout.");
      setBusyPlanId(null);
    }
  };

  const confirmCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/paypal/cancel");
      toast.success(data.message || "Subscription set to cancel at period end.");
      setShowCancelModal(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    } finally {
      setCancelling(false);
    }
  };

  const reactivate = async () => {
    try {
      const { data } = await api.post("/paypal/reactivate");
      window.location.href = data.approval_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reactivate.");
    }
  };

  const billingCopy = billingAccess?.reason === "trial"
    ? "You're on a free trial. Billing management unlocks the moment you subscribe to a paid plan — a workspace owner can start it any time from the SerpNex pricing page."
    : billingAccess?.reason === "member"
      ? "Only workspace owners can change the plan. If you're managing SEO for the workspace owner, ask them to update billing from their own account."
      : "Only workspace owners can change the plan.";

  return (
    <div className="p-8 lg:p-10 max-w-5xl">
      <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Settings</div>
      <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Account &amp; billing</h1>

      <section className="mt-8 card-crisp p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-8 w-8 rounded-md bg-[#E0E7FF] text-[#0047FF] flex items-center justify-center"><User className="h-4 w-4" /></div>
          <div className="text-lg font-display font-semibold text-[#0A0D14]">Profile</div>
        </div>
        <dl className="grid sm:grid-cols-2 gap-4 text-sm">
          <div><dt className="text-xs text-[#8A94A6] uppercase tracking-wider">Full name</dt><dd className="mt-1 text-[#0A0D14]">{user?.full_name}</dd></div>
          <div><dt className="text-xs text-[#8A94A6] uppercase tracking-wider">Email</dt><dd className="mt-1 text-[#0A0D14]">{user?.email}</dd></div>
          <div><dt className="text-xs text-[#8A94A6] uppercase tracking-wider">Company</dt><dd className="mt-1 text-[#0A0D14]">{user?.company || "—"}</dd></div>
          <div><dt className="text-xs text-[#8A94A6] uppercase tracking-wider">Role</dt><dd className="mt-1 text-[#0A0D14] capitalize">{user?.role}</dd></div>
        </dl>
      </section>

      <section className="mt-6 card-crisp p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-8 w-8 rounded-md bg-[#E0E7FF] text-[#0047FF] flex items-center justify-center"><Sparkles className="h-4 w-4" /></div>
          <div className="text-lg font-display font-semibold text-[#0A0D14]">Current plan</div>
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <span className="pill pill-accent capitalize" data-testid="current-plan-pill">{currentPlanId}</span>
          {current?.trial_ends_at && currentPlanId === "trial" && (
            <span className="text-xs text-[#525B6A]">Trial ends {new Date(current.trial_ends_at).toLocaleDateString()}</span>
          )}
          {currentIsPaid && subscription?.current_period_end && !isCancelScheduled && (
            <span className="text-xs text-[#525B6A]">Renews {new Date(subscription.current_period_end).toLocaleDateString()}</span>
          )}
          {currentIsPaid && !isCancelScheduled && (
            <button onClick={() => setShowCancelModal(true)} data-testid="cancel-paypal" className="ml-auto text-xs text-red-600 hover:text-red-700 underline">Cancel subscription</button>
          )}
        </div>

        {isCancelScheduled && (
          <div className="mt-4 border border-amber-200 bg-amber-50 rounded-md p-4 flex items-start gap-3" data-testid="cancel-banner">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#0A0D14]">Subscription ends {subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : "at period end"}</div>
              <div className="mt-1 text-xs text-[#525B6A]">You keep full <span className="capitalize">{currentPlanId}</span> access — including white-label reports, team seats, and scheduled reports — until then. After that, your account reverts to the 7-day trial limits.</div>
            </div>
            <button onClick={reactivate} data-testid="reactivate-paypal" className="btn-primary px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1.5 shrink-0">
              <RotateCcw className="h-3 w-3" /> Reactivate
            </button>
          </div>
        )}
      </section>

      <section className="mt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-8 w-8 rounded-md bg-[#E0E7FF] text-[#0047FF] flex items-center justify-center"><CreditCard className="h-4 w-4" /></div>
          <div className="text-lg font-display font-semibold text-[#0A0D14]">Change plan</div>
        </div>
        {billingAccess === null && (
          <div className="text-sm text-[#8A94A6]" data-testid="billing-loading">Checking access…</div>
        )}
        {billingAccess && !billingAccess.allowed && (
          <div className="card-crisp p-6 flex items-start gap-4" data-testid="billing-restricted">
            <div className="h-10 w-10 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center shrink-0"><Lock className="h-5 w-5" /></div>
            <div>
              <div className="text-sm font-medium text-[#0A0D14]">Billing is Owner-only.</div>
              <p className="mt-1 text-sm text-[#525B6A] max-w-2xl">{billingCopy}</p>
            </div>
          </div>
        )}
        {billingAccess?.allowed && (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="billing-plans-grid">
              {plans.map((p) => {
                const hasIntro = typeof p.intro_price_monthly === "number" && p.intro_price_monthly < p.price_monthly;
                const kind = changeKind(p.id);
                const isCurrent = kind === "current";
                const label = isCurrent ? "Current plan"
                  : kind === "upgrade" ? `Upgrade to ${p.name}`
                  : kind === "downgrade" ? `Downgrade to ${p.name}`
                  : `Subscribe with PayPal`;
                const btnClass = isCurrent
                  ? "btn-ghost cursor-default"
                  : kind === "downgrade"
                    ? "btn-ghost hover:bg-[#F1F5F9]"
                    : "btn-primary";
                return (
                  <div key={p.id} data-testid={`billing-plan-${p.id}`} className={`card-crisp p-5 flex flex-col ${isCurrent ? 'ring-2 ring-[#0047FF] shadow-[0_10px_30px_rgba(0,71,255,0.14)]' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[13px] uppercase tracking-widest text-[#8A94A6] font-medium">{p.name}</div>
                      {isCurrent && <span className="text-[10px] uppercase tracking-widest text-[#0047FF] font-semibold">Active</span>}
                    </div>
                    <div className="mt-2 flex items-baseline gap-1.5">
                      <span className="stat-value text-3xl font-bold text-[#0047FF]" data-testid={`billing-price-${p.id}`}>${hasIntro ? p.intro_price_monthly : p.price_monthly}</span>
                      {hasIntro && <span className="text-[#8A94A6] text-sm line-through">${p.price_monthly}</span>}
                      <span className="text-[#525B6A] text-xs">/ mo</span>
                    </div>
                    <div className="text-xs text-[#525B6A] mt-1">{p.keywords.toLocaleString()} keywords · unlimited projects</div>
                    <button
                      disabled={isCurrent || busyPlanId === p.id}
                      onClick={() => subscribe(p.id)}
                      data-testid={`sub-${p.id}`}
                      className={`mt-4 w-full px-3 py-2 rounded-md text-sm font-semibold ${btnClass} disabled:opacity-60`}
                    >
                      {busyPlanId === p.id ? "Redirecting to PayPal…" : label}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-[#8A94A6] mt-4">Secure checkout runs through PayPal. Downgrades and upgrades take effect immediately after PayPal confirms the change. Cancellations take effect at the end of your current billing period.</p>
          </>
        )}
      </section>

      {showCancelModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="cancel-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5" /></div>
                <div>
                  <div className="text-lg font-display font-semibold text-[#0A0D14]">Cancel your subscription?</div>
                  <div className="mt-1 text-xs text-[#8A94A6]">This can't be undone once your billing period ends.</div>
                </div>
              </div>
              <button onClick={() => setShowCancelModal(false)} className="text-[#8A94A6] hover:text-[#0A0D14]" data-testid="cancel-modal-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 text-sm text-[#525B6A] leading-relaxed">
              You'll keep full <span className="font-semibold capitalize text-[#0A0D14]">{currentPlanId}</span> access until <span className="font-semibold text-[#0A0D14]">{subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : "the end of your current billing period"}</span>.
              After that, you'll lose:
            </div>
            <ul className="mt-3 space-y-1.5 text-sm text-[#525B6A]">
              <li className="flex items-start gap-2"><span className="text-red-500 mt-0.5">•</span> White-label PDF reports</li>
              <li className="flex items-start gap-2"><span className="text-red-500 mt-0.5">•</span> Team seats &amp; shared workspaces</li>
              <li className="flex items-start gap-2"><span className="text-red-500 mt-0.5">•</span> Scheduled email reports</li>
              <li className="flex items-start gap-2"><span className="text-red-500 mt-0.5">•</span> Your paid-tier keyword allowance (dropped to 5 kw / 1 project)</li>
            </ul>
            <p className="mt-4 text-xs text-[#8A94A6]">You can restart your subscription anytime from this page — your project data won't be deleted.</p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setShowCancelModal(false)} data-testid="cancel-modal-back" className="btn-ghost px-4 py-2 rounded-md text-sm">Keep my plan</button>
              <button onClick={confirmCancel} disabled={cancelling} data-testid="cancel-modal-confirm" className="px-4 py-2 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                {cancelling ? "Cancelling…" : "Confirm cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
